import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { api, type Health } from "@/api/client";
import type { Me } from "@/lib/types";
import { SitesList } from "@/pages/SitesList";
import { SiteDetail } from "@/pages/SiteDetail";
import { Settings } from "@/pages/Settings";
import { Metrics } from "@/pages/Metrics";
import { DevLogin } from "@/pages/DevLogin";
import { FacerLogin } from "@/pages/FacerLogin";

interface AppCtx {
  me: Me;
  refreshMe: () => Promise<void>;
  toast: (msg: string) => void;
}
const Ctx = createContext<AppCtx | null>(null);
export const useApp = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp outside provider");
  return v;
};

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg(null), 3200);
  }, []);

  if (loading) return <div className="center">Загрузка…</div>;
  if (!me) {
    const mode = health?.auth_mode ?? "dev";
    if (mode === "facer") {
      const ssoError = new URLSearchParams(window.location.search).has("sso_error");
      return <FacerLogin facerUrl={health?.facer_url ?? null} ssoError={ssoError} />;
    }
    if (mode === "oidc") {
      window.location.href = "/api/auth/login";
      return <div className="center">Перенаправляем на вход…</div>;
    }
    return <DevLogin onLoggedIn={refreshMe} />;
  }

  const logout = async () => {
    await api.logout();
    setMe(null);
    navigate("/");
  };

  const isAdmin = me.perms.includes("admin");

  return (
    <Ctx.Provider value={{ me, refreshMe, toast }}>
      <div className="topbar">
        <div className="brand">
          <span className="mk">🌐</span>Сайты маркетинга
        </div>
        <div className="spacer" />
        <NavLink to="/" className={({ isActive }) => "navlink" + (isActive ? " on" : "")} end>
          Мои сайты
        </NavLink>
        <NavLink to="/metrics" className={({ isActive }) => "navlink" + (isActive ? " on" : "")}>
          Метрики
        </NavLink>
        {isAdmin && (
          <NavLink
            to="/settings"
            className={({ isActive }) => "navlink" + (isActive ? " on" : "")}
          >
            Настройка
          </NavLink>
        )}
        <div className="user" title={me.roles.join(", ")}>
          <span className="av">{initials(me.name)}</span>
          {me.name}
        </div>
        <button className="navlink" onClick={logout}>
          Выйти
        </button>
      </div>

      <Routes>
        <Route path="/" element={<SitesList />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/site/:id" element={<SiteDetail />} />
        <Route path="/settings" element={isAdmin ? <Settings /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {toastMsg && <div className="toast">{toastMsg}</div>}
    </Ctx.Provider>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}
